import { lowercase, floatify, newId } from '@/utils'
import { DateTime } from 'luxon'
import { weekdays } from '@/const'
import { firestoreAction } from 'vuexfire'
import { db, firebase, Timestamp } from '@/firebase'
import { hasIn, difference, cloneDeep } from 'lodash'

import tasks from '@/store/modules/tasks'
import vendors from '@/store/modules/vendors'
// import recipes from '@/store/modules/recipes'
import products from '@/store/modules/products'

import logs from '@/store/modules/logs'
import waste from '@/store/modules/waste'
import orders from '@/store/modules/orders'
import global from '@/store/modules/global'
import reports from '@/store/modules/reports'
import invoices from '@/store/modules/invoices'
import transfers from '@/store/modules/transfers'
import inventories from '@/store/modules/inventories'
import notifications from '@/store/modules/notifications'

const getDefaultState = () => {
  return {
    data: {},
    ref: null
  }
}

const state = getDefaultState()

const getters = {
  ref(state) {
    return state.ref
  },
  data(state) {
    return { ...state.data }
  },
  id(state) {
    try {
      return state.data.id
    } catch {
      return null
    }
  },
  shifts(state) {
    try {
      return state.data.shifts
    } catch {
      return null
    }
  },
  positions(state) {
    try {
      return state.data.positions
    } catch {
      return null
    }
  },
  name(state) {
    try {
      return state.data.name
    } catch {
      return null
    }
  },
  storeCode(state) {
    try {
      return state.data.storeCode
    } catch {
      return null
    }
  },
  address(state) {
    try {
      return state.data.address
    } catch {
      return null
    }
  },
  timezone(state) {
    try {
      return state.data.timezone
    } catch {
      return null
    }
  },
  productLocations(state) {
    try {
      return state.data.productLocations || []
    } catch {
      return []
    }
  },
  productExpenseCategories(state) {
    try {
      return state.data.productExpenseCategories || []
    } catch {
      return []
    }
  },
  notifications(state) {
    try {
      return state.data.notifications
    } catch {
      return null
    }
  },
  notificationsProductPriceChangePercent(state) {
    try {
      return state.data.notifications.priceChange.percent
    } catch {
      return null
    }
  },
  deliveryFee(state) {
    try {
      return state.data.deliveryFee
    } catch {
      return null
    }
  },
  payrollTax(state) {
    try {
      return state.data.payrollTax
    } catch {
      return null
    }
  },
  productPrices(state) {
    try {
      return state.data.productPrices
    } catch {
      return null
    }
  },
  productPriceByProductId(state) {
    return (id) => {
      try {
        return state.data.productPrices[id]
      } catch {
        return null
      }
    }
  },
  productRecipeUnitPriceByProductId(state, getters, rootState, rootGetters) {
    return (id) => {
      try {
        let genericProductId = getters['products/genericIdByProductId'](id)
        let productCost = Object.prototype.hasOwnProperty.call(
          state.data.productPrices[id],
          'cost'
        )
          ? state.data.productPrices[id].cost || 0
          : 0
        // let productYield = Object.prototype.hasOwnProperty.call(
        //   getters['products/productById'](id),
        //   'yieldPercent'
        // )
        //   ? Number(getters['products/productById'](id).yieldPercent) || 100
        //   : 100
        let productYield = Number(
          rootGetters['global/productYieldPercent'](genericProductId)
        )
        let totalRecipeUnits = Number(
          // getters['products/recipeUnitPerOrderUnitById'](id)
          rootGetters['global/productRecipeUnitPerOrderUnitById'](
            genericProductId
          )
        )
        // console.log(
        //   genericProductId,
        //   productCost,
        //   totalRecipeUnits,
        //   productYield,
        //   productCost / (totalRecipeUnits * productYield)
        // )
        // return (productCost * (productYield / 100)) / totalRecipeUnits
        return productCost / (totalRecipeUnits * productYield)
      } catch {
        return null
      }
    }
  },
  hours(state) {
    try {
      return state.data.hours
    } catch {
      return null
    }
  },
  hoursByDays(state) {
    try {
      return Object.getOwnPropertyNames(state.data)
        .filter((name) => name.indexOf('hours_') === 0)
        .sort((a, b) => a[6] - b[6])
        .map((name) => state.data[name])
    } catch {
      return []
    }
  },
  hoursByDay(state) {
    return (day) => {
      try {
        return state.data[`hours_${day}`]
      } catch {
        return null
      }
    }
  },
  // TASKS
  currentLog(state) {
    try {
      return state.data.currentLog
    } catch {
      return null
    }
  },
  monthlyLog(state) {
    try {
      return (state.data && state.data.logs && state.data.logs.month) || null
    } catch {
      return null
    }
  },
  weeklyLog(state) {
    try {
      return (state.data && state.data.logs && state.data.logs.week) || null
    } catch {
      return null
    }
  },
  tasksByShift(state) {
    return (shift) => {
      try {
        return state.data[`tasks_${shift}`]
      } catch {
        return null
      }
    }
  },
  tasksTodayByShifts(state, getters, rootState, rootGetters) {
    let weekDayName = rootGetters['time/weekDayName']
    let weekDay = rootGetters['time/weekDay']
    let monthDay = rootGetters['time/monthDay']
    try {
      // IMPROVE
      // create an array with length of shifts?
      let list = new Array(...getters['shifts'].map(() => []))
      Object.values(getters['tasks/tasks']).forEach((task) => {
        if (!(task.active == false)) {
          if (task.frequency) {
            if (
              (task.frequency == 'weekly' &&
                task.weekday != weekDay &&
                task.weekday != weekDayName) ||
              (task.frequency == 'monthly' && task.monthday != monthDay)
            ) {
              // console.log(task.frequency, task.weekday, weekDay, weekDayName)
              return
            }
          }
          list[task.shift].push(task.id)
        }
      })
      list.forEach((tasks, shift) => {
        tasks.sort(
          (a, b) =>
            getters['tasksByShift'](shift).indexOf(a) -
            getters['tasksByShift'](shift).indexOf(b)
        )
      })
      return list
    } catch {
      return []
    }
  },
  tasksByShifts(state, getters) {
    try {
      // let theseShifts = []
      // getters['shifts'].forEach((shift, index) => {
      //   let tasksThisShift = []
      //   for (const task in getters['tasks/tasks']) {
      //     if (getters['tasks/taskById'](task).shift === index) {
      //       tasksThisShift.push(task)
      //     }
      //   }
      //   theseShifts[index] = tasksThisShift
      // })
      // return theseShifts

      let list = new Array(...getters['shifts'].map(() => []))
      Object.values(getters['tasks/tasks']).forEach((task) => {
        if (!(task.active == false)) list[task.shift].push(task.id)
      })
      return list
    } catch {
      return []
    }
  },
  tasksRemaining(state, getters, rootState, rootGetters) {
    try {
      const weeklyLog = rootGetters['storeModule/logs/weeklyLog']
      const monthlyLog = rootGetters['storeModule/logs/monthlyLog']
      const tasks = rootGetters['global/tasks']

      return {
        weekly: Object.keys(tasks).filter((taskId) => {
          try {
            return (
              tasks[taskId].frequency === 'weekly' &&
              !(weeklyLog && weeklyLog[taskId] && weeklyLog[taskId].completed)
            )
          } catch {
            return false
          }
        }),
        monthly: Object.keys(tasks).filter((taskId) => {
          try {
            return (
              tasks[taskId].frequency === 'monthly' &&
              !(
                monthlyLog &&
                monthlyLog[taskId] &&
                monthlyLog[taskId].completed
              )
            )
          } catch {
            return false
          }
        })
      }
    } catch {
      return { weekly: [], monthly: [] }
    }
  },
  tasksRemainingByShifts(state, getters) {
    if (!getters.currentLog) return []
    return getters['tasksTodayByShifts'].map((tasks) => {
      return tasks.filter((task) => {
        try {
          return !getters['logs/logs'][getters.currentLog][task].completed
        } catch {
          return true
        }
      })
    })
  },
  tasksRemainingByShift: (state, getters) => {
    return (shift) => {
      try {
        return state.data[`tasks_${shift}`].filter((task) => {
          try {
            return getters['logs/logs'][getters.currentLog][task].completed
              ? false
              : true
          } catch {
            return true
          }
        })
      } catch {
        return []
      }
    }
  },
  tasksPercentCompletedByShifts(state, getters) {
    try {
      return getters['tasksByShifts'].map((taskList, index) => {
        return (
          ((taskList.length - getters['tasksRemainingByShifts'][index].length) /
            taskList.length) *
          100
        ).toFixed(0)
      })
    } catch {
      return 0
    }
  },
  //
  //
  // ORDERS
  currentOrderId(state) {
    try {
      return state.data.currentOrder
    } catch {
      return null
    }
  },
  currentOrder(state, getters) {
    try {
      return getters['orders/orderById'](getters['currentOrderId'])
    } catch {
      return null
    }
  },
  currentOrderCompleted(state, getters) {
    try {
      return !!getters['currentOrder'].completed
    } catch {
      return false
    }
  },
  dailyOrdering(state) {
    try {
      return state.data.dailyOrdering
    } catch {
      return []
    }
  },
  //
  //
  // INVENTORIES
  currentInventoryId(state) {
    try {
      return state.data.currentInventory
    } catch {
      return null
    }
  },
  currentInventory(state, getters) {
    try {
      return getters['inventories/inventories'][getters['currentInventoryId']]
    } catch {
      return null
    }
  },
  isCurrentInventoryOpen(state, getters) {
    try {
      return getters['currentInventory'].status == 'open' ? true : false
    } catch {
      return false
    }
  },
  isCurrentInventoryCompleted(state, getters) {
    // CLEAN UP
    //    use inventoryItemRemaining to reduce redundancy?
    try {
      return getters['inventoryItems'].find(
        (item) =>
          !Object.prototype.hasOwnProperty.call(
            getters['currentInventory'],
            item
          )
      )
        ? false
        : true
    } catch {
      return false
    }
  },
  inventoryItems(state, getters) {
    try {
      return getters['dailyOrdering'].filter((item) =>
        getters['products/productIdsActive'].includes(item)
      )
    } catch {
      return []
    }
  },
  inventoryItemsRemaining: (state, getters) => {
    try {
      return getters['inventoryItems'].filter(
        (item) =>
          !Object.prototype.hasOwnProperty.call(
            getters['currentInventory'],
            item
          )
      )
    } catch {
      return []
    }
  },

  // INVOICES
  currentInvoices(state, getters) {
    return getters['invoices/invoicesToday']
  },
  currentInvoicesCount(state, getters) {
    try {
      return getters['currentInvoices'].length
    } catch {
      return 0
    }
  },
  currentInvoicesRemaining(state, getters) {
    try {
      return getters['invoices/invoicesToday'].filter((id) => {
        try {
          return !getters['invoices/invoices'][id].completed
        } catch {
          return false
        }
      })
    } catch {
      return null
    }
  },
  currentInvoicesRemainingCount(state, getters) {
    try {
      return getters['currentInvoicesRemaining'].length
    } catch {
      return 0
    }
  },
  vendorsByDays(state, getters) {
    let theseDays = {}
    weekdays.forEach((day) => {
      let vendorsThisDay = []
      for (const vendor in getters['vendors/vendors']) {
        try {
          if (getters['vendors/vendors'][vendor].orderingDays.includes(day))
            vendorsThisDay.push(vendor)
        } catch {
          console.log(getters['vendors/vendors'][vendor])
        }
      }
      theseDays[day] = vendorsThisDay
    })
    return theseDays
  },
  productsAndRecipesIDs(state, getters, rootState, rootGetters) {
    let list = []
    for (const product in getters['products/productsNotHidden']) {
      list.push(product)
    }
    for (const recipe in rootGetters['recipes/recipes']) {
      list.push(recipe)
    }
    return list
  },
  localProductIdFromGenericId(state, getters) {
    // output an object with the generic id as the key and local product id as the value
    // where the local product is one where the genericProductId is the same as the local product id
    // where the local product has an active value of true
    // where the local product has a hidden value of false or null or undefined
    return (id) => {
      // Get all products that aren't hidden
      const products = getters['products/productsNotHidden']

      // Find the first matching product that:
      // 1. Has genericProductId matching the input id
      // 2. Is active
      // 3. Isn't hidden
      for (const productId in products) {
        const product = products[productId]
        if (
          product.genericProductId === id &&
          product.active !== false &&
          (product.hidden === false || product.hidden == null)
        ) {
          return productId
        }
      }

      return null
    }
  },
  localRecipeIdFromGenericId(state, getters, rootState, rootGetters) {
    return (id) => {
      const recipes = rootGetters['recipes/recipes']

      for (const recipeId in recipes) {
        const recipe = recipes[recipeId]
        if (
          recipe.genericRecipeId === id &&
          recipe.active !== false &&
          (recipe.hidden === false || recipe.hidden == null)
        ) {
          return recipeId
        }
      }

      return null
    }
  },
  recipeCosts(state, getters, rootState, rootGetters) {
    const globalProducts = rootGetters['global/products'] || {}
    const recipes = rootGetters['recipes/recipes'] || {}
    const processingStack = new Set() // Prevent infinite loops
    const costCache = new Map() // Cache recipe costs

    // Helper function to calculate recipe cost
    const calculateRecipeCost = (recipeId) => {
      // Check cache first
      if (costCache.has(recipeId)) {
        return costCache.get(recipeId)
      }

      // Prevent infinite loops from circular dependencies
      if (processingStack.has(recipeId)) {
        console.warn(`Circular dependency detected for recipe ${recipeId}`)
        return 0
      }

      processingStack.add(recipeId)
      const recipe = recipes[recipeId]
      let totalInputs = 0

      if (recipe && recipe.ingredients) {
        for (const [ingredientId, amount] of Object.entries(
          recipe.ingredients
        )) {
          if (!ingredientId) continue

          if (
            Object.prototype.hasOwnProperty.call(globalProducts, ingredientId)
          ) {
            const localId = getters['localProductIdFromGenericId'](ingredientId)
            if (localId) {
              const price =
                getters['productRecipeUnitPriceByProductId'](localId)
              if (price != null) {
                totalInputs += price * (amount || 1)
              }
            }
          } else if (rootGetters['recipes/isRecipe'](ingredientId)) {
            // FIXED: Changed from getters.recipes/isRecipe to rootGetters['recipes/isRecipe']
            const subRecipe = recipes[ingredientId]
            const subRecipeCost = calculateRecipeCost(ingredientId)
            const unitYield = (subRecipe && subRecipe.unitYield) || 1
            totalInputs += (subRecipeCost / unitYield) * (amount || 1)
          }
        }
      }

      processingStack.delete(recipeId)
      costCache.set(recipeId, totalInputs)
      return totalInputs
    }

    // Process all recipes and return as object
    const recipeIds = Object.keys(recipes)
    if (!recipeIds.length) return {}

    return recipeIds.reduce((acc, id) => {
      acc[id] = calculateRecipeCost(id)
      return acc
    }, {})
  },
  recipeUnitCosts(state, getters, rootState, rootGetters) {
    const recipes = rootGetters['recipes/recipes'] || {}
    const recipeCosts = getters['recipeCosts'] || {}

    // Process all recipes and return as object
    return Object.keys(recipeCosts).reduce((acc, recipeId) => {
      // Skip if recipe doesn't exist
      if (!recipes[recipeId]) {
        acc[recipeId] = 0
        return acc
      }

      const recipeCost = recipeCosts[recipeId]
      const unitYield = (recipes[recipeId] && recipes[recipeId].unitYield) || 1

      acc[recipeId] = recipeCost / unitYield
      console.log(
        'recipeUnitCosts',
        recipeId,
        recipeCost,
        unitYield,
        acc[recipeId]
      )
      return acc
    }, {})
  },
  recipeUnitCostById(state, getters) {
    return (id) => {
      return getters['recipeUnitCosts'][id] || 0
    }
  },
  recipeCostById(state, getters) {
    return (id) => {
      return getters['recipeCosts'][id]
    }
  },
  productOrRecipeNameById(state, getters, rootState, rootGetters) {
    return (id) => {
      try {
        if (getters['products/isProduct'](id)) {
          return getters['products/productName'](id)
        } else if (rootGetters['recipes/isRecipe'](id)) {
          // FIXED: Changed from getters['recipes/isRecipe'] to rootGetters['recipes/isRecipe']
          return rootGetters['recipes/recipeName'](id) // FIXED: Changed from getters['recipes/recipeName'] to rootGetters['recipes/recipeName']
        } else if (rootGetters['global/isProduct'](id)) {
          return rootGetters['global/genericProductName'](id)
        } else {
          return null
        }
      } catch {
        return null
      }
    }
  },
  productsByVendors(state, getters) {
    let theseVendors = {}
    for (const vendor in getters['vendors/vendors']) {
      let itemsThisVendor = []
      for (const product in getters['products/productsNotHidden']) {
        if (getters['products/productById'](product).vendor === vendor) {
          itemsThisVendor.push(product)
        }
      }
      theseVendors[vendor] = itemsThisVendor
    }
    return theseVendors
  },
  productsByVendorById(state, getters) {
    return (vendorId) => {
      return hasIn(getters['productsByVendors'], vendorId)
        ? getters['productsByVendors'][vendorId]
        : []
    }
  },
  productsByDays(state, getters) {
    let theseDays = {}
    let orderingSequence = getters['dailyOrdering']

    weekdays.forEach((day) => {
      let productsThisDay = []
      let vendors = getters['vendorsByDays'][day]
      vendors.forEach((vendor) => {
        let products = getters['productsByVendors'][vendor]
        products.forEach((product) => {
          if (getters['products/isProductActive'](product))
            productsThisDay.push(product)
        })
      })
      // sort each list to match orderingSequence
      theseDays[day] = productsThisDay.sort(function (a, b) {
        return orderingSequence.indexOf(a) - orderingSequence.indexOf(b)
      })
    })
    return theseDays
  },
  productsNotInInvoiceByInvoiceId(state, getters) {
    return (invoiceId) => {
      if (!hasIn(getters['invoices/invoiceById'](invoiceId), 'vendorId'))
        return []
      else
        return difference(
          getters['productsByVendorById'](
            getters['invoices/invoiceById'](invoiceId).vendorId
          ),
          getters['invoices/productsByInvoiceId'](invoiceId)
        )
    }
  },
  productsNotInInvoiceOrZeroedOutByInvoiceId(state, getters) {
    return (invoiceId) => {
      if (!hasIn(getters['invoices/invoiceById'](invoiceId), 'vendorId'))
        return []
      else
        return [
          ...difference(
            getters['productsByVendorById'](
              getters['invoices/invoiceById'](invoiceId).vendorId
            ),
            getters['invoices/productsByInvoiceId'](invoiceId)
          ),
          ...getters['invoices/productsZeroedOutByInvoiceId'](invoiceId)
        ]
    }
  },
  pricePerCountingUnitByProductIdByOrderUnitCost(state, getters) {
    return (productId) => {
      return (orderUnitCost) => {
        return getters['products/productById'](productId)
          ? floatify(
              orderUnitCost /
                getters['products/productById'](productId).qtyPerUnit
            )
          : 0
      }
    }
  },
  lastProductPricePerCountingUnit(state, getters) {
    return (productId) => {
      if (getters['products/isProduct'](productId)) {
        let pricePerOrderUnit = 0
        let pricePerCountingUnit = 0
        try {
          pricePerOrderUnit = getters['productPriceByProductId'](productId)
            ? getters['productPriceByProductId'](productId).cost
            : 0
          // pricePerOrderUnit = getters['invoices/lastInvoiceByVendorWithProduct'](
          //   getters['products/productById'](productId).vendor
          // )(productId)[productId]['cost']

          if (typeof pricePerOrderUnit == 'number') {
            pricePerCountingUnit = floatify(
              pricePerOrderUnit /
                getters['products/productById'](productId).qtyPerUnit
            )
            pricePerOrderUnit = floatify(pricePerOrderUnit)
          } else {
            pricePerCountingUnit = 0
            pricePerOrderUnit = 0
          }
        } catch (e) {
          console.log('Error getting last product price.', productId, e)
        }
        return pricePerCountingUnit
      }
      return 0
    }
  },
  productPricesByInvoiceId(state, getters) {
    return (invoiceId) => {
      return getters['invoices/productCostsByInvoiceById'](invoiceId).map(
        (item) => {
          return {
            ...item,
            pricePerCountingUnit: getters[
              'pricePerCountingUnitByProductIdByOrderUnitCost'
            ](item.id)(item.cost)
          }
        }
      )
    }
  },

  // input: { productId: amountEntered, productId: amountEntered, ... }
  // output: { vendorId: { productId: { amountEntered, amountOrdered }}}
  sortOrderProductsByVendor(state, getters) {
    return (input) => {
      // create a new object to return
      return Object.keys(input).reduce(function (output, thisProduct) {
        let thisVendor = ''

        // If source key (productId) is not a valid product, or doesn't have a vendorId, don't add it
        try {
          thisVendor = getters['products/productById'](thisProduct).vendor
        } catch {
          // not a valid productId, or no vendor listed under that product
          return output
        }

        // calculate amountOrdered for this product
        let amountOrdered = getters.determineProductOrderAmount(thisProduct)(
          input[thisProduct]
        )

        // if amountOrdered > 0, push to ouput object
        if (amountOrdered > 0) {
          // If the vendor hasn't been added to the output object yet,
          // create the vendor in new object
          if (!Object.prototype.hasOwnProperty.call(output, thisVendor))
            output[thisVendor] = {}

          // push the values to the new object
          output[thisVendor][thisProduct] = {
            amountEntered: input[thisProduct],
            amountOrdered
          }
        }

        // Return the object to the next item in the reduce loop
        return output
      }, {})
    }
  },
  // input: { productId: amountEntered, productId: amountEntered, ... }
  // output: { vendorId: { productId: { amountEntered, amountOrdered }}}
  sortInventoryProductsByVendor(state, getters) {
    return (input) => {
      // create a new object to return
      return Object.keys(input).reduce(function (output, thisProduct) {
        let thisVendor = ''

        // If source key (productId) is not a valid product, or doesn't have a vendorId, don't add it
        try {
          thisVendor = getters['products/productById'](thisProduct).vendor
        } catch {
          // not a valid productId, or no vendor listed under that product
          return output
        }

        // calculate amountOrdered for this product
        let amountOrdered = getters.determineProductOrderAmount(thisProduct)(
          input[thisProduct].amount
        )

        // if amountOrdered > 0, push to ouput object
        // TODO: 20250101 - Make this order for the current day, and any number of prior days, if asked
        if (
          amountOrdered > 0 &&
          getters['productsByDays']['monday'].includes(thisProduct)
        ) {
          // If the vendor hasn't been added to the output object yet,
          // create the vendor in new object
          if (!Object.prototype.hasOwnProperty.call(output, thisVendor))
            output[thisVendor] = {}

          // push the values to the new object
          output[thisVendor][thisProduct] = {
            amountEntered: input[thisProduct].amount,
            amountOrdered
          }
        }

        // Return the object to the next item in the reduce loop
        return output
      }, {})
    }
  },
  determineProductOrderAmount(state, getters) {
    return (productId) => {
      return (input) => {
        let orderAmount = 0
        let product = { ...getters['products/productById'](productId) }

        if (product.inputType === 'boolean') {
          orderAmount = input === false ? 1 : 0
        } else if (product.inputType === 'booleanTrue') {
          orderAmount = input === true ? 1 : 0
        } else if (product.inputType === 'number') {
          let calculation = (product.par - input) / product.qtyPerUnit
          orderAmount = calculation >= 1 ? Math.floor(calculation) : 0
          if (orderAmount === 0 && input === 0) orderAmount = 1
        }

        return orderAmount
      }
    }
  },
  nextDeliveryDate(state, getters) {
    return (vendorId) => {
      // find the next delivery day for a vendor after today (within in the next 7 days)
      let deliveryDate = ''

      // start one day after today
      for (let increment = 1; increment <= 7; increment++) {
        let day = lowercase(
          DateTime.utc()
            .setZone('America/New_York')
            .plus({ days: increment })
            .toFormat('cccc')
        )
        if (
          getters['vendors/vendorById'](vendorId).deliveryDays.includes(day)
        ) {
          deliveryDate = DateTime.utc()
            .setZone('America/New_York')
            .plus({ days: increment })
            .toJSDate()
          increment = 8
        }
      }

      return deliveryDate
    }
  },
  invoicesByOrderId(state, getters) {
    return (sourceOrderId) => {
      sourceOrderId = getters['currentOrderId']

      // get current order ID or fail
      if (!sourceOrderId) throw 'No SourceOrderId passed, or was not found.'

      // get current order or exit
      let invoicesByVendors = Object.assign(
        {},
        getters['orders/orderById'](sourceOrderId)
      )
      if (!Object.entries(invoicesByVendors).length === 0)
        throw `No order found with Id: ${sourceOrderId}`

      // sort valid order products by vendor
      invoicesByVendors = {
        ...getters['sortOrderProductsByVendor'](invoicesByVendors)
      }

      Object.keys(invoicesByVendors).forEach((vendorId) => {
        Object.keys(invoicesByVendors[vendorId]).forEach((productId) => {
          const existing = invoicesByVendors[vendorId][productId]
          invoicesByVendors[vendorId][productId] = {
            productId,
            name: getters['products/productName'](productId),
            invoiceAction: null,
            returnReason: null,
            returnQty: 0,
            previousCost: getters['productPriceByProductId'](productId)
              ? getters['productPriceByProductId'](productId).cost
              : 0,
            ...existing, // { amountEntered, amountOrdered }
            suggestedOrder: existing.amountOrdered
          }
        })

        // Append vendor products not included in this order (amountOrdered = 0)
        Object.values(getters['products/productsNotHidden'])
          .filter((product) => product.vendor === vendorId)
          .forEach((product) => {
            if (
              !Object.prototype.hasOwnProperty.call(
                invoicesByVendors[vendorId],
                product.id
              )
            ) {
              invoicesByVendors[vendorId][product.id] = {
                productId: product.id,
                name: product.name,
                invoiceAction: null,
                returnReason: null,
                returnQty: 0,
                previousCost: getters['productPriceByProductId'](product.id)
                  ? getters['productPriceByProductId'](product.id).cost
                  : 0,
                suggestedOrder: 0,
                amountEntered: null,
                amountOrdered: 0
              }
            }
          })

        invoicesByVendors[vendorId] = {
          previousCostTotal: Object.values(invoicesByVendors[vendorId]).reduce(
            (total, item) => total + item.previousCost * item.amountOrdered,
            0
          ),
          vendorId,
          orderId: sourceOrderId,
          orderDate: Timestamp.now().toDate(),
          deliveryDate: getters['nextDeliveryDate'](vendorId),
          vendorName: getters['vendors/vendorName'](vendorId),
          ...invoicesByVendors[vendorId]
        }

        invoicesByVendors[vendorId].email = getters['createOrderEmailBody'](
          vendorId
        )(invoicesByVendors[vendorId])
      })

      return invoicesByVendors
    }
  },
  invoicesByInventoryId(state, getters) {
    return (sourceOrderId) => {
      if (!sourceOrderId) sourceOrderId = getters['currentInventoryId']

      // get current order ID or fail
      if (!sourceOrderId) throw 'No SourceOrderId passed, or was not found.'

      // get current order or exit
      let invoicesByVendors = Object.assign(
        {},
        getters['inventories/inventoryById'](sourceOrderId)
      )
      if (!Object.entries(invoicesByVendors).length === 0)
        throw `No inventory found with Id: ${sourceOrderId}`

      // sort valid order products by vendor
      invoicesByVendors = {
        ...getters['sortInventoryProductsByVendor'](invoicesByVendors)
      }

      Object.keys(invoicesByVendors).forEach((vendorId) => {
        Object.keys(invoicesByVendors[vendorId]).forEach((productId) => {
          const existing = invoicesByVendors[vendorId][productId]
          invoicesByVendors[vendorId][productId] = {
            productId,
            name: getters['products/productName'](productId),
            invoiceAction: null,
            returnReason: null,
            returnQty: 0,
            previousCost: getters['productPriceByProductId'](productId)
              ? getters['productPriceByProductId'](productId).cost
              : 0,
            ...existing, // { amountEntered, amountOrdered }
            suggestedOrder: existing.amountOrdered
          }
        })

        // Append vendor products not included in this order (amountOrdered = 0)
        Object.values(getters['products/productsNotHidden'])
          .filter((product) => product.vendor === vendorId)
          .forEach((product) => {
            if (
              !Object.prototype.hasOwnProperty.call(
                invoicesByVendors[vendorId],
                product.id
              )
            ) {
              invoicesByVendors[vendorId][product.id] = {
                productId: product.id,
                name: product.name,
                invoiceAction: null,
                returnReason: null,
                returnQty: 0,
                previousCost: getters['productPriceByProductId'](product.id)
                  ? getters['productPriceByProductId'](product.id).cost
                  : 0,
                suggestedOrder: 0,
                amountEntered: null,
                amountOrdered: 0
              }
            }
          })

        invoicesByVendors[vendorId] = {
          previousCostTotal: Object.values(invoicesByVendors[vendorId]).reduce(
            (total, item) => total + item.previousCost * item.amountOrdered,
            0
          ),
          vendorId,
          inventoryId: sourceOrderId,
          orderDate: Timestamp.now().toDate(),
          deliveryDate: getters['nextDeliveryDate'](vendorId),
          vendorName: getters['vendors/vendorName'](vendorId),
          ...invoicesByVendors[vendorId]
        }

        invoicesByVendors[vendorId].email = getters['createOrderEmailBody'](
          vendorId
        )(invoicesByVendors[vendorId])
      })

      return invoicesByVendors
    }
  },
  invoicesForVendorIds(state, getters) {
    return (vendorIds) => {
      let invoicesByVendors = {}

      vendorIds.forEach((vendorId) => {
        invoicesByVendors[vendorId] = {}

        Object.values(getters['products/productsNotHidden'])
          .filter((product) => product.vendor === vendorId)
          .forEach((product) => {
            invoicesByVendors[vendorId][product.id] = {
              productId: product.id,
              name: product.name,
              invoiceAction: null,
              returnReason: null,
              returnQty: 0,
              previousCost: getters['productPriceByProductId'](product.id)
                ? getters['productPriceByProductId'](product.id).cost
                : 0,
              suggestedOrder: 0,
              amountEntered: null,
              amountOrdered: 0
            }
          })

        invoicesByVendors[vendorId] = {
          previousCostTotal: 0,
          vendorId,
          orderDate: Timestamp.now().toDate(),
          deliveryDate: getters['nextDeliveryDate'](vendorId),
          vendorName: getters['vendors/vendorName'](vendorId),
          ...invoicesByVendors[vendorId]
        }
      })

      return invoicesByVendors
    }
  },
  reportWeeklyTransfers(state, getters) {
    return async (date) => {
      console.log('storeModule/reportWeeklyTransfers')
      console.log('Date as passed: ', date)

      // end date
      if (!date) {
        // if no date provided, end date is the beginning of the current week
        date = DateTime.utc()
          .setZone('America/New_York')
          .startOf('week')
          .toJSDate()
      } else {
        date = DateTime.fromISO(date).setZone('America/New_York').toJSDate()
      }

      console.log('Date adjusted: ', date)

      let transfers = []

      await getters['transfers/ref']
        .where(
          'completedAt',
          '>=',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .minus({ days: 60 })
            .toJSDate()
        )
        .where(
          'completedAt',
          '<=',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .toJSDate()
        )
        .get()
        .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            let transfer = doc.data()
            transfers.push(transfer)
          })
        })
        .catch(function (error) {
          console.log(
            'Error getting transfer documents from Firestore: ',
            error
          )
        })

      let report = {
        date,
        transfers
      }
      console.log('generated weekly transfer report: ', report)

      return report
    }
  },
  reportWeeklyInvoices(state, getters, rootState, rootGetters) {
    return async (date) => {
      console.log('storeModule/reportWeeklyInvoices')
      console.log('Date as passed: ', date)

      // end date
      if (!date) {
        // if no date provided, end date is the beginning of the current week
        date = DateTime.utc()
          .setZone('America/New_York')
          .startOf('week')
          .toJSDate()
      } else {
        date = DateTime.fromISO(date).setZone('America/New_York').toJSDate()
      }

      console.log('Date adjusted: ', date)

      let invoices = []

      await getters['ref']
        .collection('invoices')
        .where(
          'deliveryDate',
          '<',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .toJSDate()
        )
        .where(
          'deliveryDate',
          '>=',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .minus({ days: 7 })
            .toJSDate()
        )
        .where('status', 'in', ['saved', 'open'])
        .get()
        .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            let invoice = doc.data()

            let categories = {
              '5480 Taxes - Paper': 0,
              '5600 COGS Processing': 0
            }

            for (let item in invoice) {
              if (
                getters['products/isProduct'](item) &&
                Object.prototype.hasOwnProperty.call(
                  invoice[item],
                  'totalCost'
                ) &&
                !isNaN(invoice[item].totalCost)
              ) {
                let itemProductCategory =
                  getters['products/categoryByProductId'](item)
                if (!itemProductCategory) continue
                else if (
                  !Object.prototype.hasOwnProperty.call(
                    categories,
                    itemProductCategory
                  )
                )
                  categories[itemProductCategory] = 0
                categories[itemProductCategory] = floatify(
                  categories[itemProductCategory] +
                    Number(invoice[item].totalCost)
                )
              } else if (item == 'tax') {
                categories['5480 Taxes - Paper'] = floatify(
                  categories['5480 Taxes - Paper'] + Number(invoice[item])
                )
              } else if (item == 'fees') {
                categories['5600 COGS Processing'] = floatify(
                  categories['5600 COGS Processing'] + Number(invoice[item])
                )
              }
            }

            invoices.push({
              id: invoice.id,
              vendorId: invoice.vendorId,
              downloadURL: invoice.downloadURL,
              billNumber: invoice.invoiceNumber,
              vendor: invoice.vendorName,
              billDate: DateTime.fromJSDate(invoice.deliveryDate.toDate())
                .setZone('America/New_York')
                .toFormat('LL/dd/yyyy'),
              terms:
                rootGetters['storeModule/vendors/terms'](invoice.vendorId) ||
                'n/a',
              expenseAccount:
                rootGetters['storeModule/vendors/expenseAccount'](
                  invoice.vendorId
                ) || 'n/a',
              lineAmount: invoice.totalCost,
              categories: categories
            })
          })
        })
        .catch(function (error) {
          console.log('Error getting invoice documents from Firestore: ', error)
        })

      let report = {
        date,
        invoices
      }
      console.log('generated weekly invoice report: ', report)

      return report
    }
  },
  reportDateRangeInvoices(state, getters, rootState, rootGetters) {
    return async ({ date, numberOfDays }) => {
      console.log('storeModule/reportWeeklyInvoices')

      console.log('Date as passed: ', date, numberOfDays)

      const fallback = (value, defaultValue) =>
        value != null ? value : defaultValue

      // end date
      if (!date) {
        // if no date provided, end date is the beginning of the current week
        date = DateTime.utc()
          .setZone('America/New_York')
          .startOf('week')
          .toJSDate()
      } else {
        date = DateTime.fromISO(date).setZone('America/New_York').toJSDate()
      }

      console.log('Date adjusted: ', date)

      let invoices = []

      await getters['ref']
        .collection('invoices')
        .where(
          'deliveryDate',
          '<',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .toJSDate()
        )
        .where(
          'deliveryDate',
          '>=',
          DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('day')
            .minus({ days: numberOfDays || 7 })
            .toJSDate()
        )
        .where('status', 'in', ['saved', 'open'])
        .get()
        .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            let invoice = doc.data()
            let lineItems = {}

            let categories = {
              '5480 Taxes - Paper': 0,
              '5600 COGS Processing': 0
            }

            for (let item in invoice) {
              if (
                getters['products/isProduct'](item) &&
                Object.prototype.hasOwnProperty.call(
                  invoice[item],
                  'totalCost'
                ) &&
                !isNaN(invoice[item].totalCost)
              ) {
                lineItems[item] = {
                  action: fallback(invoice[item].action, ''),
                  adjustment: fallback(invoice[item].adjustment, ''),
                  adjustmentCost: fallback(invoice[item].adjustmentCost, ''),
                  amountBilled: fallback(invoice[item].amountBilled, ''),
                  amountEntered: fallback(invoice[item].amountEntered, ''),
                  amountOrdered: fallback(invoice[item].amountOrdered, ''),
                  amountReceived: fallback(invoice[item].amountReceived, ''),
                  amountReturned: fallback(invoice[item].amountReturned, ''),
                  cost: fallback(invoice[item].cost, ''),
                  id: fallback(invoice[item].id, ''),
                  invoiceAction: fallback(invoice[item].invoiceAction, ''),
                  name: fallback(invoice[item].name, ''),
                  previousCost: fallback(invoice[item].previousCost, ''),
                  productId: fallback(invoice[item].productId, ''),
                  returnQty: fallback(invoice[item].returnQty, ''),
                  returnReason: fallback(invoice[item].returnReason, ''),
                  totalCost: fallback(invoice[item].totalCost, '')
                }

                let itemProductCategory =
                  getters['products/categoryByProductId'](item)

                lineItems[item].category = fallback(itemProductCategory, '')

                if (!itemProductCategory) continue
                else if (
                  !Object.prototype.hasOwnProperty.call(
                    categories,
                    itemProductCategory
                  )
                )
                  categories[itemProductCategory] = 0
                categories[itemProductCategory] = floatify(
                  categories[itemProductCategory] +
                    Number(invoice[item].totalCost)
                )
              } else if (item == 'tax') {
                categories['5480 Taxes - Paper'] = floatify(
                  categories['5480 Taxes - Paper'] + Number(invoice[item])
                )
              } else if (item == 'fees') {
                categories['5600 COGS Processing'] = floatify(
                  categories['5600 COGS Processing'] + Number(invoice[item])
                )
              }
            }

            invoices.push({
              id: fallback(invoice.id, ''),
              vendorId: fallback(invoice.vendorId, ''),
              vendorName: fallback(invoice.vendorName, ''),
              billNumber: fallback(invoice.invoiceNumber, ''),
              invoiceNumber: fallback(invoice.invoiceNumber, ''),
              billDate: invoice.deliveryDate
                ? DateTime.fromJSDate(invoice.deliveryDate.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy')
                : '',
              terms: fallback(
                rootGetters['storeModule/vendors/terms'](invoice.vendorId),
                'n/a'
              ),
              expenseAccount: fallback(
                rootGetters['storeModule/vendors/expenseAccount'](
                  invoice.vendorId
                ),
                'n/a'
              ),
              totalCost: fallback(invoice.totalCost, ''),
              categories: categories,
              adjustmentCostTotal: fallback(invoice.adjustmentCostTotal, ''),
              cancelled: fallback(invoice.cancelled, ''),
              completed: fallback(invoice.completed, ''),
              completedAt: invoice.completedAt
                ? DateTime.fromJSDate(invoice.completedAt.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy HH:mm')
                : '',
              completedBy: fallback(invoice.completedBy, ''),
              createdAt: invoice.createdAt
                ? DateTime.fromJSDate(invoice.createdAt.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy HH:mm')
                : '',
              date: invoice.date
                ? DateTime.fromJSDate(invoice.date.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy HH:mm')
                : '',
              deliveryDate: invoice.deliveryDate
                ? DateTime.fromJSDate(invoice.deliveryDate.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy')
                : '',
              discrepancy: fallback(invoice.discrepancy, ''),
              fees: fallback(invoice.fees, ''),
              orderId: fallback(invoice.orderId, ''),
              orderDate: invoice.orderDate
                ? DateTime.fromJSDate(invoice.orderDate.toDate())
                    .setZone('America/New_York')
                    .toFormat('LL/dd/yyyy')
                : '',
              previousCostTotal: fallback(invoice.previousCostTotal, ''),
              status: fallback(invoice.status, ''),
              tax: fallback(invoice.tax, ''),
              lineItems: lineItems
            })
          })
        })
        .catch(function (error) {
          console.log('Error getting invoice documents from Firestore: ', error)
        })

      let report = {
        date,
        invoices
      }
      console.log('generated weekly invoice report: ', report)

      return report
    }
  },
  createOrderEmailBody(state, getters) {
    return (vendorId) => {
      return (order) => {
        let vendor = { ...getters['vendors/vendorById'](vendorId) }
        if (vendor == null || order == null) return null

        // collect/format additional data
        let account = vendor.account ? vendor.account : getters.data.name
        let emails = vendor.emailRecipients.map((email) => {
          return { email }
        })
        let orderDate = DateTime.fromJSDate(order.orderDate)
          .setZone('America/New_York')
          .toFormat('DDDD')
        let deliveryDate = DateTime.fromJSDate(order.deliveryDate)
          .setZone('America/New_York')
          .toFormat('DDDD')

        // Helper function to handle null/undefined values
        const formatField = (value, defaultText = '') => {
          return value ? value : defaultText
        }

        let th =
          '<th style="padding-top: 0.25rem; padding-bottom: 0.25rem; padding-left: 0.5rem; padding-right: 0.5rem; border: 1px solid #ccc">'
        let td =
          '<td style="padding-top: 0.25rem; padding-bottom: 0.25rem; padding-left: 0.5rem; padding-right: 0.5rem; border: 1px solid #ccc">'

        // Build plain text version with conditional sections
        let plainTextContent = `Vendor: ${formatField(vendor.name, 'N/A')}\n`

        // Only include contact info if it exists
        if (vendor.contact) {
          plainTextContent += `Contact: ${vendor.contact}\n`
        }

        // Only include phone if it exists
        if (vendor.phone) {
          plainTextContent += `Phone: ${vendor.phone}\n`
        }

        plainTextContent += `
Order date: ${orderDate}
Delivery date: ${deliveryDate}
Account: ${account}
Address: ${getters.data.address}

${Object.keys(order)
  .filter((key) => getters['products/isProduct'](key))
  .sort((a, b) =>
    getters['products/productName'](a)
      .toLowerCase()
      .localeCompare(getters['products/productName'](b).toLowerCase())
  )
  .map((item) => {
    let product = getters['products/productById'](item)
    if (!order[item].amountOrdered > 0) return
    return ` 
        Product: ${formatField(product.name)}
        Code: ${formatField(product.code)}
        Order Unit: ${formatField(product.unit)}
        Qty: ${formatField(order[item].amountOrdered)}
      `
  })
  .join('')}`

        // Build HTML version with conditional sections
        let htmlContent = `<strong>${formatField(
          vendor.name,
          'N/A'
        )}</strong><br>`

        // Only include contact info if it exists
        if (vendor.contact) {
          htmlContent += `${vendor.contact}<br>`
        }

        // Only include phone if it exists
        if (vendor.phone) {
          htmlContent += `phone: ${vendor.phone}<br>`
        }

        htmlContent += `<br>
<strong>Order date: </strong>${orderDate}<br>
<strong>Delivery date: </strong>${deliveryDate}<br>
<br>
<strong>Account: </strong>${account}<br>
<strong>Deliver To: </strong>${getters.data.address}<br></strong>
<br>
<table style="border-collapse: collapse; text-align: left">
  <thead style="font-weight: bold; background: #eee">
    <tr>
      ${th}Product</th>
      ${th}Code</th>
      ${th}Order Unit</th>
      ${th}Qty</th>
    </tr>
  </thead>
  <tbody>
    ${Object.keys(order)
      .filter((key) => getters['products/isProduct'](key))
      .sort((a, b) =>
        getters['products/productName'](a)
          .toLowerCase()
          .localeCompare(getters['products/productName'](b).toLowerCase())
      )
      .map((item) => {
        let product = getters['products/productById'](item)
        if (!order[item].amountOrdered > 0) return
        return ` <tr>
            ${td}${formatField(product.name)}</td>
            ${td}${formatField(product.code)}</td>
            ${td}${formatField(product.unit)}</td>
            ${td}${formatField(order[item].amountOrdered)}</td>
            </tr>
          `
      })
      .join('')}
  </tbody>
</table>
`

        let msgbody = {
          to: emails,
          from: {
            email: 'orders@seoulspice.com',
            name: getters.data.name
          },
          subject: vendor.emailSubject,
          text: plainTextContent,
          html: htmlContent
        }

        return msgbody
      }
    }
  },
  storeRegion(state, getters, rootState, rootGetters) {
    const storeId = getters.id
    return rootGetters['global/storeRegionById'](storeId)
  }
}
const actions = {
  // downloadStore({getters, dispatch }) {
  //   console.log('storeModule/downloadStore')
  //   return
  // },
  // async copyRecipes(context, { storeFrom, storeTo }) {
  //   // Move all data from one store to another
  //   console.log(storeFrom, storeTo)
  //   db.collection('stores')
  //     .doc(storeFrom)
  //     .collection('recipes')
  //     .get()
  //     .then((querySnapshot) => {
  //       querySnapshot.docs.map((doc) => {
  //         let docData = doc.data()
  //         db.collection('stores')
  //           .doc(storeTo)
  //           .collection('recipes')
  //           .doc(docData.id)
  //           .set({ ...docData })
  //       })
  //     })
  //     .catch((error) => {
  //       console.log(error)
  //       return
  //     })
  // },
  async copyStore(
    { dispatch },
    { storeFrom, storeTo, storeName, storeAddress, storeNumber }
  ) {
    // Move all data from one store to another
    console.log(storeFrom, storeTo)
    db.collection('stores')
      .doc(storeFrom)
      .get()
      .then((doc) => {
        if (doc && doc.exists) {
          db.collection('stores')
            .doc(storeTo)
            .set(
              {
                ...doc.data(),
                currentInventory: '',
                currentInvoices: [],
                currentLog: '',
                currentOrder: '',
                id: storeTo,
                storeNumber: storeNumber || 0,
                address: storeAddress || '',
                name: storeName || storeTo
              },
              { merge: true }
            )
        }
      })
      .catch((error) => {
        console.log(error)
        return
      })
    db.collection('stores')
      .doc(storeFrom)
      .collection('vendors')
      .get()
      .then((querySnapshot) => {
        querySnapshot.docs.map((doc) => {
          let docData = doc.data()
          db.collection('stores')
            .doc(storeTo)
            .collection('vendors')
            .doc(docData.id)
            .set({ ...docData })
        })
      })
      .catch((error) => {
        console.log(error)
        return
      })
    db.collection('stores')
      .doc(storeFrom)
      .collection('products')
      .get()
      .then((querySnapshot) => {
        querySnapshot.docs.map((doc) => {
          let docData = doc.data()
          db.collection('stores')
            .doc(storeTo)
            .collection('products')
            .doc(docData.id)
            .set({ ...docData })
        })
      })
      .catch((error) => {
        console.log(error)
        return
      })
    db.collection('stores')
      .doc(storeFrom)
      .collection('tasks')
      .get()
      .then((querySnapshot) => {
        querySnapshot.docs.map((doc) => {
          let docData = doc.data()
          db.collection('stores')
            .doc(storeTo)
            .collection('tasks')
            .doc(docData.id)
            .set({ ...docData })
        })
      })
      .catch((error) => {
        console.log(error)
        return
      })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'inventories'
    })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'invoices'
    })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'logs'
    })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'orders'
    })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'reports'
    })
    dispatch('createBlankCollection', {
      storeId: storeTo,
      collectionName: 'waste'
    })
    // dispatch('createBlankCollection', {
    //   storeId: storeTo,
    //   collectionName: 'recipes'
    // })
  },
  async copyStoreTasks({ getters, dispatch }, { storeFrom, cleanup = 'soft' }) {
    const sourceDoc = await db.collection('stores').doc(storeFrom).get()
    if (!sourceDoc.exists) throw new Error(`Source store ${storeFrom} not found`)
    const sourceData = sourceDoc.data()

    // Clean up existing destination tasks before writing
    if (cleanup) {
      const existingTasks = await db
        .collection('stores')
        .doc(getters.id)
        .collection('tasks')
        .get()
      const cleanupBatch = db.batch()
      existingTasks.docs.forEach((doc) => {
        if (cleanup === 'hard') {
          cleanupBatch.delete(doc.ref)
        } else {
          cleanupBatch.update(doc.ref, {
            status: 'deleted',
            active: false,
            updatedAt: Timestamp.now()
          })
        }
      })
      await cleanupBatch.commit()
    }

    // Copy task documents (same IDs)
    const sourceTasks = await db
      .collection('stores')
      .doc(storeFrom)
      .collection('tasks')
      .get()
    const batch = db.batch()
    sourceTasks.docs.forEach((doc) => {
      const destRef = db
        .collection('stores')
        .doc(getters.id)
        .collection('tasks')
        .doc(doc.id)
      batch.set(destRef, doc.data())
    })
    await batch.commit()

    // Collect tasks_N shift arrays from source store document
    const taskListUpdates = {}
    Object.keys(sourceData).forEach((key) => {
      if (/^tasks_\d+$/.test(key)) {
        taskListUpdates[key] = sourceData[key]
      }
    })

    // Replace only notifications.tasks on the destination, preserving other notification settings
    const destNotifications = cloneDeep(getters['notifications'])
    destNotifications.tasks = (sourceData.notifications &&
      sourceData.notifications.tasks) || { active: false, triggers: [] }

    return dispatch('updateItem', {
      ...taskListUpdates,
      notifications: destNotifications
    })
  },
  createBlankCollection(context, { storeId, collectionName }) {
    db.collection('stores')
      .doc(storeId)
      .collection(collectionName)
      .doc('init')
      .set({
        init: 'init'
      })
      .then(() => console.log('Created collection: ', collectionName))
      .catch((error) => {
        console.log(error)
      })
  },
  // INVOICES
  // createInvoice: async function({ dispatch }, data) {
  //   console.log('storeModule/createInvoice', data)
  //   return dispatch('invoices/createInvoice', data)
  // },
  createNotification(context, data) {
    console.log('storeModule/createNotification')
    db.collection('notifications')
      .doc(data.id)
      .set(data)
      .catch((err) => console.log(err))
  },
  async createProductPriceChangesNotification({ getters, dispatch }, items) {
    console.log('storeModule/createProductPriceChangeNotification', items)
    // items: [
    //   { productId, productName, priceOld, priceNew, percentChange }
    // ]

    if (
      (getters['notifications'].priceChange &&
        !getters['notifications'].priceChange.active) ||
      !items
    ) {
      console.log('priceChange notifications not found, or not active')
      return
    }

    const workers = {
      phone: (options) =>
        dispatch('createNotification', {
          worker: 'sendText',
          options: {
            phone: options.phone,
            body: options.body
          },
          status: 'scheduled',
          id: options.id,
          performAt: options.now
        }),
      email: (options) =>
        dispatch('createNotification', {
          worker: 'sendEmail',
          options: {
            email: options.email,
            subject: `${options.storeName} Price Change Alert!`,
            body: options.body
          },
          status: 'scheduled',
          id: options.id,
          performAt: options.now
        })
    }

    let jobs = []
    getters['notifications'].priceChange.phoneRecipients.forEach((phone) => {
      jobs.push(
        workers.phone({
          phone,
          body: `Price change alert! The following product prices have changed by more than ${
            getters['notificationsProductPriceChangePercent']
          }% at ${getters['name']}: ${items
            .map(
              (item) =>
                `\n ${item.productName} (${
                  item.priceOld > item.priceNew ? '-' : ''
                }${item.percentChange}%)`
            )
            .join(', ')}`,
          id: newId(),
          now: Timestamp.now()
        })
      )
    })
    getters['notifications'].priceChange.emailRecipients.forEach((email) => {
      jobs.push(
        workers.email({
          email,
          storeName: getters['name'],
          body: `The following product prices have changed by more than ${
            getters['notificationsProductPriceChangePercent']
          }% at ${getters['name']}: ${items
            .map(
              (item) =>
                `\n ${item.productName} (Was: $${item.priceOld}, Now: $${
                  item.priceNew
                }, Change: ${item.priceOld > item.priceNew ? '-' : ''}${
                  item.percentChange
                }%)`
            )
            .join(', ')}`,
          id: newId(),
          now: Timestamp.now()
        })
      )
    })

    return await Promise.all(jobs)
  },
  // addProductToInvoice({ getters, dispatch }, { invoiceId, productId }) {
  //   console.log('storeModule/addProductToInvoice', { invoiceId, productId })

  //   let data = {
  //     [productId]: {
  //       amountOrdered: false,
  //       name: getters['products/productName'](productId),
  //       productId,
  //       amountEntered: 0,
  //       invoiceAction: null,
  //       returnQty: 0,
  //       returnReason: null,
  //       cost: 0,
  //       totalCost: 0
  //     }
  //   }
  //   if (
  //     getters['invoices/vendorIdByInvoiceId'](invoiceId) ==
  //     getters['products/vendorIdByProductId'](productId)
  //   )
  //     return dispatch('invoices/updateItem', { id: invoiceId, data })
  //   else {
  //     console.log({
  //       invoiceId,
  //       vendorIdByInvoiceId: getters['invoices/vendorIdByInvoiceId'](invoiceId),
  //       productId,
  //       vendorIdByProductId: getters['products/vendorIdByProductId'](productId)
  //     })
  //     throw Error("vendorId's do not match.")
  //   }
  // },

  // INVENTORIES
  createNewInventory: async function ({ dispatch }) {
    return dispatch('inventories/create').then((id) =>
      dispatch('updateItem', { currentInventory: id })
    )
  },
  createPastInventory: async function ({ getters, dispatch }, { date }) {
    let items = {}
    getters['inventoryItems'].forEach((item) => {
      let price =
        getters['productPriceByProductId'](item) &&
        Object.prototype.hasOwnProperty.call(
          getters['productPriceByProductId'](item),
          'cost'
        )
          ? getters['productPriceByProductId'](item).cost
          : 0
      // TODO - add usage?
      // let usage =

      items[item] = {
        id: item,
        price,
        // usage,
        amount: 0
      }
    })
    return date
      ? dispatch('inventories/create', {
          completed: true,
          status: 'saved',
          date,
          ...items
        })
      : new Error('no date provided, or date is not a valid inventory date')
  },
  fixPastInventoryProductPrices: async function (
    { getters, dispatch },
    inventoryId
  ) {
    // this function gathers prices from these sources:
    // 1) all invoices for the week prior to the inventory date
    // 2) the previous week's inventory (which is essentially a log of count & productPrices at that time)
    // 3) the current productPrices list
    // and then sets the inventoryId's product prices with the price from the
    // source with higher importance as listed above

    // then assigns the product

    let productPrices = { ...getters['productPrices'] }
    let invoicePrices = {}
    let invoiceUnitsReceived = {}
    let inventory = null
    let previousInventory = null
    let date = null

    let newPrices = {} // just here for debugging purposes

    // get inventory & date (end date)
    try {
      inventory = { ...getters['inventories/inventoryById'](inventoryId) }
      if (!inventory) throw new Error('Invalid InventoryID.')
      date = inventory.date.seconds * 1000
    } catch {
      throw new Error('Invalid InventoryID or inventory date.')
    }

    // get previous Inventory
    await getters['ref']
      .collection('inventories')
      .where(
        'date',
        '<',
        DateTime.fromSeconds(date / 1000)
          .setZone('America/New_York')
          .endOf('day')
          .minus({ days: 7 })
          .toJSDate()
      )
      .where(
        'date',
        '>',
        DateTime.fromSeconds(date / 1000)
          .setZone('America/New_York')
          .startOf('day')
          .minus({ days: 7 })
          .toJSDate()
      )
      .where('status', 'in', ['saved', 'open'])
      .limit(1)
      .get()
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (doc) {
          previousInventory = { ...doc.data() }
        })
      })
      .catch(function (error) {
        console.log('Error getting invoice documents from Firestore: ', error)
      })

    // loop invoices from week of end date
    // orderBy("date") beginning to end so newer prices overwrite older ones (good)
    // collect product prices in {}

    await getters['ref']
      .collection('invoices')
      .where(
        'deliveryDate',
        '<',
        DateTime.fromSeconds(date / 1000)
          .setZone('America/New_York')
          .startOf('day')
          .toJSDate()
      )
      .where(
        'deliveryDate',
        '>=',
        DateTime.fromSeconds(date / 1000)
          .setZone('America/New_York')
          .startOf('day')
          .minus({ days: 7 })
          .toJSDate()
      )
      //.where('status', 'in', ['saved', 'open'])
      .orderBy('deliveryDate')
      .get()
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (doc) {
          let invoice = doc.data()
          console.log(
            'invoice: ',
            DateTime.fromSeconds(invoice.date.seconds)
              .setZone('America/New_York')
              .toJSDate(),
            invoice.vendorName,
            invoice
          )
          for (let item in invoice) {
            if (
              getters['products/isProduct'](item) &&
              Object.prototype.hasOwnProperty.call(invoice[item], 'cost') &&
              !isNaN(invoice[item].cost) &&
              Number(invoice[item].cost) > 0
            ) {
              invoicePrices[item] = Number(invoice[item].cost)
            }
            if (
              getters['products/isProduct'](item) &&
              Object.prototype.hasOwnProperty.call(
                invoice[item],
                'amountReceived'
              ) &&
              Object.prototype.hasOwnProperty.call(
                invoice[item],
                'amountReturned'
              ) &&
              !isNaN(invoice[item].amountReceived) &&
              !isNaN(invoice[item].amountReturned) &&
              getters['products/qtyPerUnitByProductId'](item)
            ) {
              if (
                !Object.prototype.hasOwnProperty.call(
                  invoiceUnitsReceived,
                  item
                )
              )
                invoiceUnitsReceived[item] = 0
              invoiceUnitsReceived[item] += Number(
                invoice[item].amountReceived *
                  getters['products/qtyPerUnitByProductId'](item)
              )
              invoiceUnitsReceived[item] -= Number(
                invoice[item].amountReturned *
                  getters['products/qtyPerUnitByProductId'](item)
              )
            }
          }
        })
        console.log('productPrices from invoices:', invoicePrices)
        console.log('invoiceUnitsReceived: ', { ...invoiceUnitsReceived })
      })
      .catch(function (error) {
        console.log('Error getting invoice documents from Firestore: ', error)
      })

    // for each product item in inventory

    // if either (in order of importance)
    // 1) price was found in invoices
    // 2) price was found in previous inventory
    // 3) price was found in productPrices
    // replace the inventory price
    //

    for (let item in inventory) {
      if (!getters['products/isProduct'](item)) continue

      if (
        Object.prototype.hasOwnProperty.call(invoicePrices, item) &&
        invoicePrices[item] &&
        !isNaN(invoicePrices[item]) &&
        Number(invoicePrices[item]) > 0
      ) {
        if (Number(inventory[item].price) !== Number(invoicePrices[item]))
          newPrices[item] = invoicePrices[item]
        // if price was found in invoices, set price
        // inventory[item].price = Number(invoicePrices[item])
      } else if (
        previousInventory &&
        Object.prototype.hasOwnProperty.call(previousInventory, item) &&
        Object.prototype.hasOwnProperty.call(previousInventory[item], 'cost') &&
        previousInventory[item].cost &&
        !isNaN(previousInventory[item].cost) &&
        Number(previousInventory[item].cost) > 0
      ) {
        if (
          Number(inventory[item].price) !== Number(previousInventory[item].cost)
        )
          newPrices[item] = previousInventory[item].cost
        // inventory[item].price = Number(previousInventory[item].cost)
      } else if (
        Object.prototype.hasOwnProperty.call(productPrices, item) &&
        Object.prototype.hasOwnProperty.call(productPrices[item], 'cost') &&
        productPrices[item].cost &&
        !isNaN(productPrices[item].cost) &&
        Number(productPrices[item].cost) > 0
      ) {
        if (Number(inventory[item].price) !== Number(productPrices[item].cost))
          newPrices[item] = productPrices[item].cost
        // if price was not found in invoices
        // set price to current invoicePrice (could go to later weeks too?)
        // inventory[item].price = Number(productPrices[item].cost)
      }

      if (
        previousInventory &&
        Object.prototype.hasOwnProperty.call(previousInventory, item) &&
        Object.prototype.hasOwnProperty.call(
          previousInventory[item],
          'amount'
        ) &&
        !isNaN(previousInventory[item].amount)
      ) {
        if (!Object.prototype.hasOwnProperty.call(invoiceUnitsReceived, item))
          invoiceUnitsReceived[item] = 0
        invoiceUnitsReceived[item] += previousInventory[item].amount
      }

      if (
        Object.prototype.hasOwnProperty.call(inventory[item], 'amount') &&
        !isNaN(inventory[item].amount)
      ) {
        if (!Object.prototype.hasOwnProperty.call(invoiceUnitsReceived, item))
          invoiceUnitsReceived[item] = 0
        invoiceUnitsReceived[item] -= inventory[item].amount
      }

      inventory[item].unitsUsed = invoiceUnitsReceived[item]
    }

    // update inventory items
    console.log('changes made: ', newPrices)
    console.log('invoiceUnitsReceived: ', invoiceUnitsReceived)
    // console.log(inventory)
    return dispatch('inventories/updateItem', {
      id: inventoryId,
      data: inventory
    })
  },
  deleteCurrentInventory({ getters, dispatch }) {
    return dispatch('inventories/delete', getters['currentInventoryId']).then(
      () => {
        getters['ref'].update({
          currentInventory: ''
        })
      }
    )
  },
  closeInventory({ getters, dispatch }, id) {
    console.log('storeModule/closeInventory', id)
    if (!id) return
    dispatch('inventories/updateItem', {
      id: id,
      data: {
        status: 'saved',
        completedAt: Timestamp.now(),
        date: Timestamp.now(),
        completed: true
      }
    })
  },
  clearCurrentInventoryId({ dispatch }) {
    dispatch('updateCurrentInventoryId', '')
  },
  updateCurrentInventoryItem({ getters, dispatch }, data) {
    let [[id, amount]] = Object.entries(data)
    let price =
      getters['productPrices'][id] && getters['productPrices'][id].cost
        ? getters['productPrices'][id].cost || 0
        : 0
    console.log(price)
    return dispatch('inventories/updateItem', {
      id: getters['currentInventoryId'],
      data: {
        [id]: {
          id,
          amount,
          price
        }
      }
    })
  },

  // ORDERS
  createNewOrder: async function ({ dispatch }) {
    return dispatch('orders/create').then((id) =>
      dispatch('updateCurrentOrderId', id)
    )
  },
  updateCurrentOrderItem({ getters, dispatch }, data) {
    return dispatch('orders/updateItem', {
      id: getters['currentOrderId'],
      data
    })
  },
  deleteCurrentOrder({ getters, dispatch }) {
    const currentOrderId = getters['currentOrderId']
    if (currentOrderId) {
      return dispatch('orders/delete', currentOrderId).then(() => {
        dispatch('clearCurrentOrderId')
      })
    } else {
      return Promise.resolve()
    }
  },
  saveCurrentOrder({ getters, dispatch }) {
    dispatch('saveOrder', getters['currentOrderId']).then(() => {
      dispatch('clearCurrentOrderId')
    })
  },
  saveOrder({ getters, dispatch }, id) {
    if (!id) return
    console.log('storeModule/saveOrder', id)
    dispatch('orders/save', {
      id,
      data: {
        completed: true
      }
    })
    if (id == getters['currentOrderId']) dispatch('clearCurrentOrderId')
  },
  clearCurrentOrderId({ dispatch }) {
    dispatch('updateCurrentOrderId', '')
  },
  updateCurrentOrderId({ dispatch }, id = '') {
    return dispatch('updateItem', { currentOrder: id })
  },
  updateCurrentInventoryId({ dispatch }, id = '') {
    console.log('storeModule/updateCurrentInventoryId', id)
    return dispatch('updateItem', { currentInventory: id })
  },
  updateOrderingSequence({ dispatch }, list) {
    return dispatch('updateItem', { dailyOrdering: list })
  },
  repairOrderingDaily: async function ({ getters, dispatch }) {
    // start with exisiting dailyOrdering array to maintain order
    let dailyOrderingClone = new Set(getters['dailyOrdering'])

    // add any products that are not already there and are not hidden
    getters['products/productIdsNotHidden'].forEach((productId) => {
      if (!dailyOrderingClone.has(productId)) dailyOrderingClone.add(productId)
    })

    // remove any product that is hidden
    dailyOrderingClone.forEach((productId) => {
      if (!getters['products/productIdsNotHidden'].includes(productId))
        dailyOrderingClone.delete(productId)
    })

    dispatch('updateOrderingSequence', Array.from(dailyOrderingClone))
  },

  // PRODUCTS
  createProduct: async function ({ dispatch }, data) {
    console.log('storeModule/createProduct', data)
    return dispatch('products/create', data).then(() =>
      dispatch('repairOrderingDaily')
    )
  },
  repairProductCategories: async function (
    { dispatch, getters },
    { from, to }
  ) {
    // CHANGES ALL PRODUCTS WITH CATEGORY: 'FROM' to 'TO'
    console.log('storeModule/repairProductCategories', { from, to })
    // let categories = new Set()
    Object.values(getters['products/data']).forEach((item) => {
      if (
        item &&
        Object.prototype.hasOwnProperty.call(item, 'id') &&
        Object.prototype.hasOwnProperty.call(item, 'category') &&
        // categories.add(item.category)

        item.category == from
      ) {
        to
          ? dispatch('updateProduct', {
              id: item.id,
              data: { category: to }
            })
          : console.log(item)
      }
    })
    // console.log(categories)
    return
  },
  updateProduct: async function ({ dispatch }, { id, data }) {
    console.log('storeModule/updateProduct', { id, data })
    return dispatch('products/updateItem', { id, data })
  },
  deleteProduct: async function ({ dispatch }, id) {
    console.log('storeModule/deleteProduct', id)
    return dispatch('products/updateItem', {
      id,
      data: { hidden: true }
    }).then(() => dispatch('repairOrderingDaily'))
  },
  updateProductPrice({ dispatch, getters }, { id, cost }) {
    console.log('storeModule/updateProductPrice', { id, cost })
    let productPrices = { ...getters['productPrices'] }
    productPrices[id] = {
      id,
      cost,
      pricePerCountingUnit:
        getters['pricePerCountingUnitByProductIdByOrderUnitCost'](id)(cost)
    }
    dispatch('updateItem', { productPrices })
  },

  // NOTIFICATIONS
  updateNotificationType({ dispatch, getters }, { type, data }) {
    console.log('storeModule/updateNotificationType', { type, data })
    let notifications = { ...getters['notifications'] }
    notifications[type] = {
      ...data
    }
    dispatch('updateItem', { notifications })
  },

  // LOGS
  createNewLog: async function ({ getters, dispatch }, data) {
    console.log('storeModule/createNewLog', data)
    return dispatch('logs/create', data).then((id) => {
      const updates = {}
      if (data.type === 'daily') {
        updates.currentLog = id
      } else if (data.type === 'weekly') {
        updates['logs.week'] = id
      } else if (data.type === 'monthly') {
        updates['logs.month'] = id
      }
      return getters.ref.update(updates)
    })
  },
  createMonthlyLog: async function ({ getters, dispatch }) {
    console.log('storeModule/createMonthlyLog')
    return dispatch('logs/create', { type: 'monthly' }).then((id) =>
      getters.ref.update({
        'logs.month': id
      })
    )
  },
  createWeeklyLog: async function ({ getters, dispatch }) {
    console.log('storeModule/createWeeklyLog')
    return dispatch('logs/create', { type: 'weekly' }).then((id) =>
      getters.ref.update({
        'logs.week': id
      })
    )
  },
  updateCurrentLogItem({ getters, dispatch }, data) {
    return dispatch('logs/updateItem', {
      id: getters['currentLog'],
      data
    })
  },
  updateWeeklyLogItem({ getters, dispatch }, data) {
    return dispatch('logs/updateItem', {
      id: getters['weeklyLog'],
      data
    })
  },
  updateMonthlyLogItem({ getters, dispatch }, data) {
    return dispatch('logs/updateItem', {
      id: getters['monthlyLog'],
      data
    })
  },

  // REPORTS
  async generateReport(
    { getters, dispatch },
    {
      salesInStore = [0, 0, 0, 0, 0, 0, 0],
      salesDelivery = [0, 0, 0, 0, 0, 0, 0],
      guestsInStore = [0, 0, 0, 0, 0, 0, 0],
      guestsDelivery = [0, 0, 0, 0, 0, 0, 0],
      // totalPayrollManagement = 0,
      payrollHourly = [0, 0, 0, 0, 0, 0, 0],
      payrollManagement = [0, 0, 0, 0, 0, 0, 0],
      feesPayroll = 0,
      feesDelivery = 0,
      date = null,
      id = null
    } = {}
  ) {
    console.log('storeModule/generateReport')

    const arrSum = (arr) =>
      arr.reduce((a, b) => {
        return floatify(Number(a) + Number(b))
      }, 0)

    const arrNumber = (arr) => arr.map((value) => floatify(Number(value)))

    console.log('Date as passed: ', date)

    // end date
    if (!date) {
      // if no date provided, end date is the beginning of the current week
      date = DateTime.setZone('America/NewYork').startOf('week').toJSDate()
    } else {
      date = DateTime.fromJSDate(date).setZone('America/New_York').toJSDate()
    }

    console.log('Date adjusted: ', date)

    // fix percent inputs
    // delivery fee multiplier should be negative (-) because delivery sales are positive by nature (+)
    feesDelivery = -Math.abs(Number(feesDelivery))
    if (Math.abs(Number(feesDelivery)) > 1) feesDelivery = feesDelivery / 100
    // payroll fee multiplier is positive (+) because payroll values should be negative (-)
    feesPayroll = Math.abs(Number(feesPayroll))
    if (Math.abs(Number(feesPayroll)) > 1) feesPayroll = feesPayroll / 100

    // fix array lengths
    // fix for negative values
    for (let i = 0; i < 7; i++) {
      if (!salesInStore[i]) salesInStore[i] = 0
      if (!salesDelivery[i]) salesDelivery[i] = 0
      if (!guestsInStore[i]) guestsInStore[i] = 0
      if (!guestsDelivery[i]) guestsDelivery[i] = 0
      payrollHourly[i] = payrollHourly[i]
        ? floatify(Math.abs(payrollHourly[i]))
        : 0
      payrollManagement[i] = payrollManagement[i]
        ? floatify(Math.abs(payrollManagement[i]))
        : 0
    }

    // fix payrollManagement salaries averaged over 7 days
    // let payrollManagement = [0, 0, 0, 0, 0, 0, 0]
    // for (let i = 0; i < 7; i++) {
    //   payrollManagement[i] = floatify(Math.abs(totalPayrollManagement) / 7)
    // }

    //
    //
    //
    //
    //
    //
    guestsInStore = arrNumber(guestsInStore)
    guestsDelivery = arrNumber(guestsDelivery)
    let guestsTotals = [0, 0, 0, 0, 0, 0, 0].map((value, index) =>
      floatify(Number(guestsInStore[index]) + Number(guestsDelivery[index]))
    )
    let guestsAverageTransactions = [0, 0, 0, 0, 0, 0, 0]
    //
    let guestsInStoreTotal = arrSum(guestsInStore)
    let guestsDeliveryTotal = arrSum(guestsDelivery)
    let guestsTotal = arrSum(guestsTotals)
    //
    let guestsInStorePercent = guestsTotal
      ? floatify(guestsInStoreTotal / guestsTotal)
      : 0
    let guestsDeliveryPercent = guestsTotal
      ? floatify(guestsDeliveryTotal / guestsTotal)
      : 0
    //
    let guests = {
      inStore: guestsInStore,
      delivery: guestsDelivery,
      totals: guestsTotals,
      averageTransactions: guestsAverageTransactions,
      //
      inStoreTotal: guestsInStoreTotal,
      deliveryTotal: guestsDeliveryTotal,
      total: guestsTotal,
      //
      inStorePercent: guestsInStorePercent,
      deliveryPercent: guestsDeliveryPercent
    }

    //
    //
    //
    //
    //
    //
    salesInStore = arrNumber(salesInStore)
    salesDelivery = arrNumber(salesDelivery)
    let salesDeliveryFees = [0, 0, 0, 0, 0, 0, 0].map((value, index) =>
      floatify(Number(salesDelivery[index]) * feesDelivery + value)
    )
    let salesTotals = salesDeliveryFees.map((value, index) =>
      floatify(
        Number(value) +
          Number(salesInStore[index]) +
          Number(salesDelivery[index])
      )
    )
    //
    let salesInStoreTotal = arrSum(salesInStore)
    let salesDeliveryTotal = arrSum(salesDelivery)
    let salesDeliveryFeesTotal = arrSum(salesDeliveryFees)
    let salesTotal = floatify(arrSum(salesTotals))
    //
    let salesInStorePercent = salesTotal
      ? floatify(salesInStoreTotal / salesTotal)
      : 0
    let salesDeliveryPercent = salesTotal
      ? floatify((salesDeliveryTotal + salesDeliveryFeesTotal) / salesTotal)
      : 0
    //
    let salesInStorePerGuest = guestsInStoreTotal
      ? floatify(salesInStoreTotal / guestsInStoreTotal)
      : 0
    let salesDeliveryPerGuest = guestsDeliveryTotal
      ? floatify(
          (salesDeliveryTotal + salesDeliveryFeesTotal) / guestsDeliveryTotal
        )
      : 0
    let salesDeliveryFeesPerGuest = guestsDeliveryTotal
      ? floatify(salesDeliveryFeesTotal / guestsDeliveryTotal)
      : 0
    let salesTotalPerGuest = guestsTotal
      ? floatify(salesTotal / guestsTotal)
      : 0
    //
    let sales = {
      fees: feesDelivery,
      //
      inStore: salesInStore,
      delivery: salesDelivery,
      deliveryFees: salesDeliveryFees,
      totals: salesTotals,
      //
      inStoreTotal: salesInStoreTotal,
      deliveryTotal: salesDeliveryTotal,
      deliveryFeesTotal: salesDeliveryFeesTotal,
      total: salesTotal,
      //
      inStorePercent: salesInStorePercent,
      deliveryPercent: salesDeliveryPercent,
      //
      inStorePerGuest: salesInStorePerGuest,
      deliveryPerGuest: salesDeliveryPerGuest,
      deliveryFeesPerGuest: salesDeliveryFeesPerGuest,
      totalPerGuest: salesTotalPerGuest
    }

    payrollManagement = arrNumber(payrollManagement)
    payrollHourly = arrNumber(payrollHourly)
    let payrollTaxes = [0, 0, 0, 0, 0, 0, 0].map((value, index) =>
      floatify(
        (Number(payrollManagement[index]) + Number(payrollHourly[index])) *
          Number(feesPayroll)
      )
    )
    let payrollTotals = [0, 0, 0, 0, 0, 0, 0].map((value, index) =>
      floatify(
        Number(payrollTaxes[index]) +
          Number(payrollManagement[index]) +
          Number(payrollHourly[index])
      )
    )
    //
    let payrollManagementTotal = arrSum(payrollManagement)
    let payrollHourlyTotal = arrSum(payrollHourly)
    let payrollTaxesTotal = arrSum(payrollTaxes)
    let payrollTotal = arrSum(payrollTotals)
    //
    let payrollManagementPercent = salesTotal
      ? Math.abs(floatify(payrollManagementTotal / salesTotal))
      : 0
    let payrollHourlyPercent = salesTotal
      ? Math.abs(floatify(payrollHourlyTotal / salesTotal))
      : 0
    let payrollTaxesPercent = salesTotal
      ? Math.abs(floatify(payrollTaxesTotal / salesTotal))
      : 0
    let payrollTotalPercent = salesTotal
      ? Math.abs(floatify(payrollTotal / salesTotal))
      : 0
    //
    let payrollManagementPerGuest = guestsTotal
      ? floatify(payrollManagementTotal / guestsTotal)
      : 0
    let payrollHourlyPerGuest = guestsTotal
      ? floatify(payrollHourlyTotal / guestsTotal)
      : 0
    let payrollTaxesPerGuest = guestsTotal
      ? floatify(payrollTaxesTotal / guestsTotal)
      : 0
    let payrollTotalPerGuest = guestsTotal
      ? floatify(payrollTotal / guestsTotal)
      : 0
    //
    let payroll = {
      fees: feesPayroll,
      //
      management: payrollManagement,
      hourly: payrollHourly,
      taxes: payrollTaxes,
      totals: payrollTotals,
      //
      managementTotal: payrollManagementTotal,
      hourlyTotal: payrollHourlyTotal,
      taxesTotal: payrollTaxesTotal,
      total: payrollTotal,
      //
      managementPercent: payrollManagementPercent,
      hourlyPercent: payrollHourlyPercent,
      taxesPercent: payrollTaxesPercent,
      totalPercent: payrollTotalPercent,
      //
      managementPerGuest: payrollManagementPerGuest,
      hourlyPerGuest: payrollHourlyPerGuest,
      taxesPerGuest: payrollTaxesPerGuest,
      totalPerGuest: payrollTotalPerGuest
    }

    //
    //
    //
    //
    //
    //
    let inventoryBeginning = {}
    let inventoryBeginningId = ''

    let inventoryEnding = {}
    let inventoryEndingId = ''

    let invoiceIds = []

    let inventoryBeginningFood = 0,
      inventoryBeginningOther = 0,
      inventoryBeginningTotal = 0,
      inventoryEndingFood = 0,
      inventoryEndingOther = 0,
      inventoryEndingTotal = 0,
      inventoryDifferenceFood = 0,
      inventoryDifferenceOther = 0,
      inventoryDifferenceTotal = 0,
      invoicesFood = 0,
      invoicesOther = 0,
      invoicesTotal = 0,
      cogsFoodTotal = 0,
      cogsOtherTotal = 0,
      cogsTotal = 0,
      cogsFoodPercent = 0,
      cogsOtherPercent = 0,
      cogsTotalPercent = 0,
      cogsFoodPerGuest = 0,
      cogsOtherPerGuest = 0,
      cogsTotalPerGuest = 0

    let timeline = []
    let timelineFood = []
    let timelineOther = []
    let productCategories = Object.values(getters['products/data']).reduce(
      (a, product) => ({ ...a, [product.id]: product.category || '' }),
      {}
    )
    console.log(productCategories)

    console.log(
      `getting invoices from: 
      ${DateTime.fromJSDate(date)
        .setZone('America/New_York')
        .startOf('day')
        .minus({ days: 7 })
        .toJSDate()} 
      to ${DateTime.fromJSDate(date)
        .setZone('America/New_York')
        .startOf('day')
        .toJSDate()}`
    )

    await getters['ref']
      .collection('invoices')
      .where(
        'deliveryDate',
        '<',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .startOf('day')
          .toJSDate()
      )
      .where(
        'deliveryDate',
        '>=',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .startOf('day')
          .minus({ days: 7 })
          .toJSDate()
      )
      .where('status', 'in', ['saved', 'open'])
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          let invoice = doc.data()
          invoiceIds.push(invoice.id)

          let fees = Number(-invoice.fees || 0)
          let tax = Number(-invoice.tax || 0)
          invoicesOther += fees
          invoicesOther += tax

          for (let item in invoice) {
            if (
              invoice[item] &&
              Object.prototype.hasOwnProperty.call(
                invoice[item],
                'totalCost'
              ) &&
              !isNaN(invoice[item].totalCost)
            ) {
              switch (productCategories[item]) {
                case '5210 Baked Goods':
                case '5220 Beverages':
                case '5232 Pantry':
                case '5233 Fruits and Vegetables':
                case '5241 Beef':
                case '5242 Chicken':
                case '5243 Eggs':
                case '5244 Pork':
                case '5245 Tofu':
                  invoicesFood = floatify(
                    invoicesFood - Number(invoice[item].totalCost)
                  )
                  timelineFood.push(invoicesFood)
                  break
                default:
                  invoicesOther = floatify(
                    invoicesOther - Number(invoice[item].totalCost)
                  )
                  timelineOther.push(invoicesOther)
              }
            }
          }

          invoicesTotal = floatify(Number(invoicesFood) + Number(invoicesOther))
          timeline.push(invoicesTotal)
        })
        console.log(timeline, timelineFood, timelineOther)
      })
      .catch(function (error) {
        console.log('Error getting invoice documents from Firestore: ', error)
      })

    console.log(
      `Getting Inventory from ${DateTime.fromJSDate(date)
        .setZone('America/New_York')
        .minus({ days: 7 })
        .startOf('day')
        .toJSDate()} to ${DateTime.fromJSDate(date)
        .setZone('America/New_York')
        .minus({ days: 7 })
        .endOf('day')
        .toJSDate()}`
    )

    await getters['ref']
      .collection('inventories')
      .where(
        'date',
        '>=',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .minus({ days: 7 })
          .startOf('day')
          .toJSDate()
      )
      .where(
        'date',
        '<=',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .minus({ days: 7 })
          .endOf('day')
          .toJSDate()
      )
      .where('status', 'in', ['saved', 'open'])
      .limit(1)
      .get()
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (doc) {
          inventoryBeginning = doc.data()
          inventoryBeginningId = inventoryBeginning.id

          for (let item in inventoryBeginning) {
            let price, amountEntered, qtyPerUnit, each, value
            if (
              getters['products/isProduct'](item) &&
              'price' in inventoryBeginning[item]
            ) {
              try {
                price = inventoryBeginning[item].price
                amountEntered = inventoryBeginning[item].amount
                qtyPerUnit = getters['products/qtyPerUnitByProductId'](item)
                if (typeof price == 'number' && !isNaN(price)) {
                  each = floatify(price / qtyPerUnit)
                  value = floatify(each * amountEntered)
                } else {
                  price = ''
                }
              } catch {
                console.log('No price listed for product ', item)
              }

              if (
                [
                  '5210 Baked Goods',
                  '5220 Beverages',
                  '5232 Pantry',
                  '5233 Fruits and Vegetables',
                  '5241 Beef',
                  '5242 Chicken',
                  '5243 Eggs',
                  '5244 Pork',
                  '5245 Tofu'
                ].includes(getters['products/categoryByProductId'](item))
              )
                inventoryBeginningFood = floatify(
                  inventoryBeginningFood - Number(value)
                )
              else
                inventoryBeginningOther = floatify(
                  inventoryBeginningOther - Number(value)
                )
            }
          }
          inventoryBeginningTotal = floatify(
            Number(inventoryBeginningFood) + Number(inventoryBeginningOther)
          )
        })
      })
      .catch(function (error) {
        console.log('Error getting documents: ', error)
      })

    await getters['ref']
      .collection('inventories')
      .where(
        'date',
        '>=',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .startOf('day')
          .toJSDate()
      )
      .where(
        'date',
        '<=',
        DateTime.fromJSDate(date)
          .setZone('America/New_York')
          .endOf('day')
          .toJSDate()
      )
      .where('status', 'in', ['saved', 'open'])
      .limit(1)
      .get()
      .then(function (querySnapshot) {
        querySnapshot.forEach(function (doc) {
          inventoryEnding = doc.data()
          inventoryEndingId = inventoryEnding.id

          for (let item in inventoryEnding) {
            let price, amountEntered, qtyPerUnit, each, value
            if (
              getters['products/isProduct'](item) &&
              'price' in inventoryEnding[item]
            ) {
              try {
                price = inventoryEnding[item].price
                amountEntered = inventoryEnding[item].amount
                qtyPerUnit = getters['products/qtyPerUnitByProductId'](item)
                if (typeof price == 'number' && !isNaN(price)) {
                  each = floatify(price / qtyPerUnit)
                  value = floatify(each * amountEntered)
                } else {
                  price = ''
                }
              } catch {
                console.log('No price listed for product ', item)
              }

              if (
                [
                  '5210 Baked Goods',
                  '5220 Beverages',
                  '5232 Pantry',
                  '5233 Fruits and Vegetables',
                  '5241 Beef',
                  '5242 Chicken',
                  '5243 Eggs',
                  '5244 Pork',
                  '5245 Tofu'
                ].includes(getters['products/categoryByProductId'](item))
              )
                inventoryEndingFood = floatify(
                  inventoryEndingFood - Number(value)
                )
              else
                inventoryEndingOther = floatify(
                  inventoryEndingOther - Number(value)
                )
            }
          }

          inventoryEndingTotal = floatify(
            Number(inventoryEndingFood) + Number(inventoryEndingOther)
          )
        })
      })
      .catch(function (error) {
        console.log('Error getting documents: ', error)
      })

    inventoryDifferenceFood = floatify(
      Number(inventoryBeginningFood) - Number(inventoryEndingFood)
    )
    inventoryDifferenceOther = floatify(
      Number(inventoryBeginningOther) - Number(inventoryEndingOther)
    )
    inventoryDifferenceTotal = floatify(
      Number(inventoryBeginningTotal) - Number(inventoryEndingTotal)
    )

    cogsFoodTotal = floatify(
      Number(inventoryDifferenceFood) + Number(invoicesFood)
    )
    cogsOtherTotal = floatify(
      Number(inventoryDifferenceOther) + Number(invoicesOther)
    )
    cogsTotal = floatify(
      Number(inventoryDifferenceTotal) + Number(invoicesTotal)
    )

    cogsFoodPercent = salesTotal
      ? Math.abs(floatify(Number(cogsFoodTotal / salesTotal)))
      : 0
    cogsOtherPercent = salesTotal
      ? Math.abs(floatify(Number(cogsOtherTotal / salesTotal)))
      : 0
    cogsTotalPercent = salesTotal
      ? Math.abs(floatify(Number(cogsTotal / salesTotal)))
      : 0

    cogsFoodPerGuest = guestsTotal
      ? floatify(Number(cogsFoodTotal / guestsTotal))
      : 0
    cogsOtherPerGuest = guestsTotal
      ? floatify(Number(cogsOtherTotal / guestsTotal))
      : 0
    cogsTotalPerGuest = guestsTotal
      ? floatify(Number(cogsTotal / guestsTotal))
      : 0

    let cogs = {
      //
      inventoryBeginning,
      inventoryBeginningId,
      inventoryEnding,
      inventoryEndingId,
      invoiceIds,
      //
      inventoryBeginningFood,
      inventoryBeginningOther,
      inventoryBeginningTotal,
      //
      inventoryEndingFood,
      inventoryEndingOther,
      inventoryEndingTotal,
      //
      inventoryDifferenceFood,
      inventoryDifferenceOther,
      inventoryDifferenceTotal,
      //
      invoicesFood,
      invoicesOther,
      invoicesTotal,
      //
      foodTotal: cogsFoodTotal,
      otherTotal: cogsOtherTotal,
      total: cogsTotal,
      //
      foodPercent: cogsFoodPercent,
      otherPercent: cogsOtherPercent,
      totalPercent: cogsTotalPercent,
      //
      foodPerGuest: cogsFoodPerGuest,
      otherPerGuest: cogsOtherPerGuest,
      totalPerGuest: cogsTotalPerGuest
      //
    }

    let primeCost = floatify(Number(cogsTotal) + -Number(payrollTotal))
    let primeCostPercent = salesTotal
      ? floatify(Number(-primeCost / salesTotal))
      : 0
    let primeCostPerGuest = guestsTotal
      ? floatify(Number(-primeCost / guestsTotal))
      : 0
    let grossMargin = floatify(Number(salesTotal) + Number(primeCost))
    let grossMarginPercent = salesTotal
      ? Math.abs(floatify(Number(grossMargin / salesTotal)))
      : 0
    let grossMarginPerGuest = guestsTotal
      ? floatify(Number(grossMargin / guestsTotal))
      : 0

    let report = {
      date,
      //
      guests,
      sales,
      payroll,
      cogs,
      //
      primeCost,
      primeCostPercent,
      primeCostPerGuest,
      //
      grossMargin,
      grossMarginPercent,
      grossMarginPerGuest
    }

    id
      ? console.log('updating report: ', report, id)
      : console.log('generated report: ', report)
    return id
      ? dispatch('reports/updateItem', { id, data: report })
      : dispatch('reports/create', report)
  },

  // TASKS
  createTask({ dispatch }, data) {
    // don't need ID, auto-generated by tasks/create
    console.log('storeModule/createTask', data)
    return dispatch('tasks/create', data).then((id) =>
      dispatch('addTaskToShift', { shift: data.shift, id })
    )
  },
  updateTask({ dispatch, getters }, { id, data }) {
    console.log('storeModule/updateTask', { id, data })
    let oldShift = getters['tasks/taskById'](id).shift
    let newShift = data.shift
    let oldNotification = getters['tasks/taskById'](id).notification || null
    let newNotification = data.notification
    let oldNotificationHour =
      getters['tasks/taskById'](id).notificationHour || null
    let newNotificationHour = data.notificationHour
    console.log({ oldNotificationHour, oldNotification })
    console.log(
      getters.notifications.tasks.triggers.find((task) => task.id == id)
    )
    return dispatch('tasks/updateItem', { id, data }).then(() => {
      if (
        oldNotification == null ||
        oldNotificationHour == null ||
        oldNotification != newNotification ||
        oldNotificationHour != newNotificationHour ||
        !getters.notifications.tasks.triggers.find((task) => task.id == id)
      ) {
        dispatch('updateTaskNotificationTrigger', {
          id,
          active: data.notification || false,
          hour: data.notificationHour || 0,
          data: data
        })
      }
      if (newShift != oldShift) {
        dispatch('removeTaskFromShift', {
          shift: oldShift,
          id: id
        })
        dispatch('addTaskToShift', {
          shift: newShift,
          id: id
        })
      }
    })
  },
  updateTaskNotificationTrigger(
    { dispatch, getters },
    { id, active = true, hour = 0, data }
  ) {
    console.log('storeModule/updateTaskNotificationTrigger', {
      id,
      active,
      hour,
      data
    })
    // todo: add check that id is a real task
    // todo: validate data

    // clone notification object, triggers, and the oldTrigger
    let notifications = cloneDeep(getters['notifications'])
    let triggers = notifications.tasks.triggers

    // copy oldTrigger, or undefined
    let oldTrigger = triggers.find((trigger) => trigger.id == id)

    // remove oldTrigger
    triggers = triggers.filter((trigger) => trigger.id != id)

    // if active, replace new trigger
    if (active) {
      triggers.push({
        id,
        ...data,
        hour
      })
    }

    // replace all triggers to notification object
    notifications.tasks.triggers = triggers

    // rewrite notifications if:
    //  trigger is new & is active
    //  trigger is old and hour is different
    //  trigger is old & is not active
    if (
      (!oldTrigger && active == true) ||
      (oldTrigger && oldTrigger.hour != hour) ||
      (oldTrigger && active == false)
    )
      dispatch('updateItem', { notifications })

    // let fixHour = function(hour, adjustment = 5) {
    //   if (typeof hour != 'number' || isNaN(hour))
    //     throw Error('Hour not a number.')
    //   if (hour > 23 || hour < 0) throw Error('Hour is not between 0 & 23.')
    //   hour += adjustment
    //   if (hour > 24) hour -= 24
    //   if (hour < 0) hour += 24
    //   return hour
    // }
  },
  updateTaskNotificationTriggers({ dispatch, getters }) {
    let notifications = cloneDeep(getters['notifications'])
    console.dir(notifications)

    let triggers = cloneDeep(notifications.tasks.triggers)
    let mapTriggers = triggers.map((trigger) => {
      return { ...getters['tasks/taskById'](trigger.id), hour: trigger.hour }
    })
    console.dir(mapTriggers)

    notifications.tasks.triggers = cloneDeep(mapTriggers)
    console.dir(notifications)

    return dispatch('updateItem', { notifications })
  },
  repairTaskNotificationTriggers({ dispatch, getters }) {
    console.log('storeModule/repairTaskNotificationTriggers')

    let notifications = cloneDeep(getters['notifications'])
    let triggers = {}

    notifications.tasks.triggers.forEach((trigger) => {
      triggers[trigger.id] = {
        id: trigger.id,
        hour: trigger.hour
      }
    })
    console.log('current notification triggers: ', triggers)

    // add tasks to notifications, if not already there
    Object.values(getters['tasks/tasks']).forEach((task) => {
      if (task.notification && task.notificationHour) {
        let active = Object.hasOwnProperty.call(task, 'active')
          ? task.active
            ? task.active
            : false
          : true
        if (!triggers[task.id] && active) {
          console.log(
            'adding notification trigger for task: ',
            getters['tasks/tasks'][task.id]
          )
          dispatch('updateTaskNotificationTrigger', {
            id: task.id,
            active,
            hour: task.notificationHour,
            data: getters['tasks/tasks'][task.id]
          })
        }
      }
    })

    // remove tasks from notifications if needed
    Object.values(triggers).forEach((trigger) => {
      if (
        getters['tasks/tasks'][trigger.id] &&
        Object.hasOwnProperty.call(
          getters['tasks/tasks'][trigger.id],
          'active'
        ) &&
        getters['tasks/tasks'][trigger.id].active == false
      ) {
        console.log(
          'removing notification trigger for task: ',
          getters['tasks/tasks'][trigger.id]
        )
        dispatch('updateTaskNotificationTrigger', {
          id: trigger.id,
          active: getters['tasks/tasks'][trigger.id].active,
          hour: getters['tasks/tasks'][trigger.id].notificationHour,
          data: getters['tasks/tasks'][trigger.id]
        })
      }
    })
  },
  repairTaskNotificationTriggers2({ dispatch, getters }) {
    console.log('storeModule/repairTaskNotificationTriggers2')

    // Clone the entire notifications object
    let notifications = cloneDeep(getters['notifications'])

    // Get all tasks
    const tasks = getters['tasks/tasks']

    // First, filter out existing triggers that should be removed
    notifications.tasks.triggers = notifications.tasks.triggers.filter(
      (trigger) => {
        const task = tasks[trigger.id]
        return (
          task &&
          task.status !== 'deleted' &&
          task.active !== false &&
          task.notification !== false
        )
      }
    )

    // Then, add or update tasks that should have notifications
    Object.values(tasks).forEach((task) => {
      // Check if task should have notifications
      if (
        task.notification === true &&
        task.active !== false &&
        task.status !== 'deleted' &&
        task.notificationHour != null
      ) {
        // Check if task is already in triggers
        const existingTriggerIndex = notifications.tasks.triggers.findIndex(
          (t) => t.id === task.id
        )

        // Create the new trigger object with latest task data
        const newTrigger = {
          id: task.id,
          hour: task.notificationHour,
          ...task
        }

        if (existingTriggerIndex === -1) {
          // If not in triggers, add it
          notifications.tasks.triggers.push(newTrigger)
        } else {
          // If already in triggers, replace with latest data
          notifications.tasks.triggers[existingTriggerIndex] = newTrigger
        }
      }
    })

    // Make a single update to the notifications object
    return dispatch('updateItem', { notifications })
  },
  deleteTask({ dispatch, getters }, id) {
    console.log('storeModule/deleteTask', id)
    let oldShift = getters['tasks/taskById'](id).shift
    return Promise.all([
      dispatch('tasks/updateItem', {
        id: id,
        data: { status: 'deleted', active: false }
      }),
      dispatch('removeTaskFromShift', {
        id: id,
        shift: oldShift
      }),
      dispatch('updateTaskNotificationTrigger', {
        id,
        active: false
      })
    ])
  },
  removeTaskFromShift({ dispatch }, { shift, id }) {
    return dispatch('updateTaskList', {
      shift: shift,
      list: firebase.firestore.FieldValue.arrayRemove(id)
    })
  },
  addTaskToShift({ dispatch }, { shift, id }) {
    return dispatch('updateTaskList', {
      shift: shift,
      list: firebase.firestore.FieldValue.arrayUnion(id)
    })
  },
  updateTaskList({ dispatch }, { shift, list }) {
    return dispatch('updateItem', { [`tasks_${shift}`]: list })
  },

  // Repair task arrays by ensuring all active tasks are in their correct shift arrays
  repairTaskArrays({ dispatch, getters }) {
    console.log('storeModule/repairTaskArrays')

    // Get all active tasks
    const activeTasks = Object.values(getters['tasks/tasks']).filter(
      (task) => task.active !== false && task.status !== 'deleted'
    )

    // For each shift, check if tasks are in the correct array
    getters['shifts'].forEach((_, shiftIndex) => {
      const currentShiftTasks = getters['tasksByShift'](shiftIndex) || []
      const tasksForThisShift = activeTasks.filter(
        (task) => task.shift === shiftIndex
      )

      // Find tasks that should be in this shift but aren't
      const missingTasks = tasksForThisShift.filter(
        (task) => !currentShiftTasks.includes(task.id)
      )

      // Find tasks that are in this shift but shouldn't be
      const tasksToRemove = currentShiftTasks.filter((taskId) => {
        const task = getters['tasks/tasks'][taskId]
        return (
          !task ||
          task.active === false ||
          task.status === 'deleted' ||
          task.shift !== shiftIndex
        )
      })

      // If we have changes to make
      if (missingTasks.length > 0 || tasksToRemove.length > 0) {
        // Start with current tasks, remove ones that shouldn't be there
        let updatedTasks = currentShiftTasks.filter(
          (taskId) => !tasksToRemove.includes(taskId)
        )

        // Add missing tasks to the end
        updatedTasks = [...updatedTasks, ...missingTasks.map((task) => task.id)]

        dispatch('updateTaskList', {
          shift: shiftIndex,
          list: updatedTasks
        })
      }
    })
  },

  // MISC
  bindStore: firestoreAction(
    ({ dispatch, commit, state, bindFirestoreRef }, { storeId }) => {
      commit('setRef', db.collection('stores').doc(storeId))
      return Promise.all([
        bindFirestoreRef('data', state.ref),
        dispatch('orders/bindStore', { storeId }),
        dispatch('invoices/bindStore', { storeId }),
        dispatch('inventories/bindStore', { storeId }),
        dispatch('logs/bindStore', { storeId }),
        dispatch('products/bindStore', { storeId }),
        dispatch('tasks/bindStore', { storeId }),
        dispatch('vendors/bindStore', { storeId }),
        dispatch('reports/bindStore', { storeId }),
        dispatch('notifications/bindStore', { storeId }),
        // dispatch('recipes/bindStore', { storeId }),
        dispatch('waste/bindStore', { storeId }),
        dispatch('transfers/bindStore'),
        dispatch('global/bindStore')
        // dispatch('recipes/bindStore')
      ])
    }
  ),
  unbindStore: firestoreAction(({ dispatch, unbindFirestoreRef }) => {
    return Promise.all([
      unbindFirestoreRef('data'),
      dispatch('orders/unbindStore'),
      dispatch('invoices/unbindStore'),
      dispatch('inventories/unbindStore'),
      dispatch('logs/unbindStore'),
      dispatch('products/unbindStore'),
      dispatch('tasks/unbindStore'),
      dispatch('vendors/unbindStore'),
      dispatch('reports/unbindStore'),
      dispatch('notifications/unbindStore'),
      // dispatch('recipes/unbindStore'),
      dispatch('transfers/unbindStore'),
      dispatch('waste/unbindStore'),
      dispatch('global/unbindStore')
      // dispatch('recipes/unbindStore')
    ])
  }),
  updateItem({ getters }, data) {
    console.log('storeModule/updateItem', data)
    return getters.ref.update(data)
  },
  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}
const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
  // setRecipeCosts(state, recipeCosts) {
  //   state.recipeCosts = recipeCosts
  // },
  // clearRecipeCosts(state) {
  //   state.recipeCosts = {}
  // }
}
const storeModule = {
  namespaced: true,

  state,
  getters,
  mutations, //...defaultMutations(state)
  actions,
  modules: {
    logs,
    orders,
    reports,
    invoices,
    inventories,
    tasks,
    vendors,
    // recipes,
    products,
    notifications,
    transfers,
    waste,
    global
  }
}

export default storeModule
